import { AlertCircle, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';

/*
 * The empty state, and its opposite.
 *
 * The prototype does empty states well and consistently, and it is a big part
 * of why it feels finished — centred icon, one-line statement, one-line hint,
 * one action. That is preserved.
 *
 * `ErrorState` sits next to it deliberately. **A failed request is an error,
 * never an empty state** (CLAUDE.md §4). Defect P22: the prototype's employee
 * client-history endpoint 500s and the UI renders "No jobs found for this
 * client" underneath a header reading "2 Total Jobs" — it tells you there is
 * nothing there, while showing you a count that says otherwise. Having both
 * components here means picking one is a decision someone makes rather than a
 * default they fall into.
 */

export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
}: {
  icon?: LucideIcon;
  /** One line, stating the fact. "No jobs scheduled for today." */
  title: string;
  /** One line, what to do about it. */
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="px-6 py-12 text-center">
      {Icon ? (
        <div className="mx-auto grid size-12 place-items-center rounded-full bg-honey-50">
          <Icon aria-hidden="true" className="size-6 text-bark-700" />
        </div>
      ) : null}
      <p className="mt-4 text-h3 text-ink-900">{title}</p>
      {hint ? <p className="mx-auto mt-1.5 max-w-md text-body text-ink-500">{hint}</p> : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}

/**
 * A request that failed.
 *
 * Says so, and offers to try again. It does **not** say "no results" — the
 * difference between "there is nothing here" and "we could not find out" is the
 * difference between a manager moving on and a manager acting on a list that is
 * missing rows.
 */
export function ErrorState({
  title = 'That could not be loaded',
  hint = 'Something went wrong on our side. Try again — if it keeps happening, tell whoever looks after this.',
  onRetry,
}: {
  title?: string;
  hint?: ReactNode;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="px-6 py-12 text-center">
      <div className="mx-auto grid size-12 place-items-center rounded-full bg-red-50">
        <AlertCircle aria-hidden="true" className="size-6 text-status-priority-text" />
      </div>
      <p className="mt-4 text-h3 text-ink-900">{title}</p>
      <p className="mx-auto mt-1.5 max-w-md text-body text-ink-500">{hint}</p>
      {onRetry ? (
        <div className="mt-5 flex justify-center">
          <Button variant="outline" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}
